// Seed data for the in-browser mock API.
// Dates are generated relative to "today" so the demo always has upcoming and past visits.
// Replace / remove once the real backend is connected (REACT_APP_USE_MOCK=false).
import { addDays, today, toDateKey } from '../../utils/date';

const rel = (n) => toDateKey(addDays(today(), n));

export const MOCK_OTP = '123456';

export const clinic = {
  id: 'demo-clinic',
  name: 'Sample Dental Clinic',
  shortName: 'Sample Dental',
  logoUrl: '/clinic-logo.png', // set per clinic; null shows a tooth mark
  primaryColor: '#5B7C5F',
  secondaryColor: '#3E5A45',
  openingHours: 'Mon–Sat, 9:00 AM – 6:00 PM',
  openingHoursDetail: [
    { days: 'Monday – Friday', hours: '9:00 AM – 6:00 PM' },
    { days: 'Saturday', hours: '9:00 AM – 6:00 PM' },
    { days: 'Sunday & public holidays', hours: 'Closed' },
  ],
  address: 'No. 1, Jalan Contoh, 93000 Kuching, Sarawak',
  phone: { countryCode: '+60', number: '82123456' },
  email: 'hello@example.com',
  latitude: 1.5535,
  longitude: 110.3593,
  currency: 'RM',
  autoConfirm: false,
  cancellationCutoffHours: 2,
  bookingWindowDays: 90,
  slotMinutes: 30,
  workingDays: [1, 2, 3, 4, 5, 6], // 0 = Sunday
};

export const landing = {
  badge: 'Book online, anytime',
  title: 'See your doctor, without the queue.',
  subtitle: 'Book appointments, get reminders and keep your visit history in one place.',
  features: [
    {
      icon: 'calendar',
      title: 'Book any time',
      body: 'See real availability and choose a slot that suits you, day or night.',
    },
    {
      icon: 'history',
      title: 'Your appointments, in one place',
      body: 'Upcoming and past appointments are saved to your account.',
    },
    {
      icon: 'reminder',
      title: 'Reminders by SMS',
      body: 'Get a reminder before every appointment so you never miss one.',
    },
  ],
  steps: [
    'Log in with your mobile number',
    'Choose a doctor, date and time',
    'Visit the clinic at your chosen time',
  ],
};

// fullyBookedDays: how many days ahead the doctor's diary is effectively full (drives mock availability).
export const doctors = [
  {
    id: 'd1',
    name: 'Dr. Lim Wei Jie',
    specialty: 'General practice',
    qualifications: 'MBBS (Malaya), MRCGP (UK)',
    experienceYears: 12,
    languages: ['English', 'Malay', 'Mandarin'],
    bio: 'Dr. Lim looks after patients of all ages, with a focus on preventive care and long-term follow-up for chronic conditions.',
    focusAreas: ['Preventive care', 'Chronic disease follow-up', 'Health screening'],
    consultationHours: 'Mon–Sat, 9:00 AM – 6:00 PM',
    consultationFee: 60,
    workingDays: [1, 2, 3, 4, 5, 6],
    fullyBookedDays: 2,
    featured: true,
    featuredRank: 1,
    photoUrl: null,
  },
  {
    id: 'd2',
    name: 'Dr. Nurul Huda',
    specialty: 'General practice',
    qualifications: 'MD (UKM)',
    experienceYears: 8,
    languages: ['English', 'Malay'],
    bio: 'Dr. Nurul Huda sees walk-in style general consultations, women’s health concerns and family check-ups.',
    focusAreas: ['Women’s health', 'Family medicine', 'Vaccinations'],
    consultationHours: 'Mon–Fri, 9:00 AM – 6:00 PM',
    consultationFee: 60,
    workingDays: [1, 2, 3, 4, 5],
    fullyBookedDays: 13,
    featured: false,
    photoUrl: null,
  },
  {
    id: 'd3',
    name: 'Dr. Ahmad Faris',
    specialty: 'Cardiology',
    qualifications: 'MBBS (IMU), MRCP (UK)',
    experienceYears: 15,
    languages: ['English', 'Malay'],
    bio: 'Dr. Ahmad Faris manages heart conditions, blood pressure and cholesterol, and reviews ECG and cardiac test results.',
    focusAreas: ['Hypertension', 'Heart rhythm', 'Cardiac screening'],
    consultationHours: 'Mon–Sat, 9:00 AM – 6:00 PM',
    consultationFee: 150,
    workingDays: [1, 2, 3, 4, 5, 6],
    fullyBookedDays: 19,
    featured: true,
    featuredRank: 3,
    photoUrl: null,
  },
  {
    id: 'd4',
    name: 'Dr. Priya Nair',
    specialty: 'Dermatology',
    qualifications: 'MBBS (Manipal), MRCP Derm (UK)',
    experienceYears: 10,
    languages: ['English', 'Malay', 'Tamil'],
    bio: 'Dr. Priya treats skin, hair and nail conditions for adults and children, from acne and eczema to skin checks.',
    focusAreas: ['Acne & eczema', 'Skin checks', 'Hair & scalp'],
    consultationHours: 'Mon–Sat, 9:00 AM – 6:00 PM',
    consultationFee: 120,
    workingDays: [1, 2, 3, 4, 5, 6],
    fullyBookedDays: 15,
    featured: true,
    featuredRank: 2,
    photoUrl: null,
  },
  {
    id: 'd5',
    name: 'Dr. Sarah Wong',
    specialty: 'Paediatrics',
    qualifications: 'MBBS (Malaya), MRCPCH (UK)',
    experienceYears: 9,
    languages: ['English', 'Mandarin'],
    bio: 'Dr. Sarah cares for babies, children and teenagers, including growth checks, vaccinations and childhood illnesses.',
    focusAreas: ['Child development', 'Vaccinations', 'Childhood asthma'],
    consultationHours: 'Mon–Sat, 9:00 AM – 6:00 PM',
    consultationFee: 90,
    workingDays: [1, 2, 3, 4, 5, 6],
    fullyBookedDays: 14,
    featured: false,
    photoUrl: null,
  },
  {
    id: 'd6',
    name: 'Dr. Daniel Ting',
    specialty: 'Orthopaedics',
    qualifications: 'MBBS (UNIMAS), MS Ortho (UKM)',
    experienceYears: 14,
    languages: ['English', 'Mandarin'],
    bio: 'Dr. Daniel treats bone, joint and sports injuries, and supports patients through recovery and rehabilitation.',
    focusAreas: ['Sports injuries', 'Knee & shoulder', 'Back pain'],
    consultationHours: 'Mon–Fri, 9:00 AM – 6:00 PM',
    consultationFee: 150,
    workingDays: [1, 2, 3, 4, 5],
    fullyBookedDays: 23,
    featured: false,
    photoUrl: null,
  },
  {
    id: 'd7',
    name: 'Dr. Melissa Jau',
    specialty: 'ENT',
    qualifications: 'MD (UNIMAS), MS ORL-HNS (USM)',
    experienceYears: 11,
    languages: ['English', 'Malay', 'Iban'],
    bio: 'Dr. Melissa treats ear, nose and throat conditions, including sinus problems, hearing concerns and tonsillitis.',
    focusAreas: ['Sinus problems', 'Hearing', 'Throat & tonsils'],
    consultationHours: 'Mon–Sat, 9:00 AM – 6:00 PM',
    consultationFee: 120,
    workingDays: [1, 2, 3, 4, 5, 6],
    fullyBookedDays: 13,
    featured: false,
    photoUrl: null,
  },
];

export const users = [
  {
    id: 'u1',
    name: 'Aisyah Rahman',
    preferredName: 'Aisyah',
    countryCode: '+60',
    phone: '123456789',
    password: 'password123',
    email: 'aisyah@example.com',
    icNumber: '900101135566',
    dateOfBirth: '1990-01-01',
    gender: 'female',
    smsReminders: true,
    createdAt: rel(-400),
  },
];

const createdAt = (n) => `${rel(n)}T10:00:00`;

export const appointments = [
  { id: 'a1', userId: 'u1', doctorId: 'd1', date: rel(0), time: '10:30', duration: 30, status: 'confirmed', visitType: 'Follow-up', reason: 'Follow-up on blood test results', createdAt: createdAt(-18) },
  { id: 'a2', userId: 'u1', doctorId: 'd4', date: rel(3), time: '15:00', duration: 30, status: 'confirmed', visitType: null, reason: '', createdAt: createdAt(-20) },
  { id: 'a3', userId: 'u1', doctorId: 'd3', date: rel(15), time: '09:00', duration: 30, status: 'pending', visitType: null, reason: 'Recurring chest tightness after exercise', createdAt: createdAt(-1) },
  { id: 'a4', userId: 'u1', doctorId: 'd1', date: rel(-22), time: '11:00', duration: 30, status: 'completed', visitType: null, reason: 'Fever and sore throat', createdAt: createdAt(-40) },
  { id: 'a5', userId: 'u1', doctorId: 'd7', date: rel(-64), time: '14:30', duration: 30, status: 'completed', visitType: null, reason: 'Blocked nose for two weeks', createdAt: createdAt(-80) },
  { id: 'a6', userId: 'u1', doctorId: 'd4', date: rel(-110), time: '10:00', duration: 30, status: 'completed', visitType: null, reason: 'Skin rash on arms', createdAt: createdAt(-130) },
  { id: 'a7', userId: 'u1', doctorId: 'd5', date: rel(-10), time: '16:00', duration: 30, status: 'cancelled', visitType: null, reason: '', cancelReason: 'Schedule conflict', createdAt: createdAt(-30) },
];


export const notifications = [
  { id: 'n1', userId: 'u1', type: 'reminder', title: 'Appointment today', body: 'Dr. Lim Wei Jie at 10:30 AM. Please arrive 10 minutes early.', appointmentId: 'a1', createdAt: `${rel(0)}T08:00:00`, read: false },
  { id: 'n2', userId: 'u1', type: 'request', title: 'Booking request received', body: 'Your request with Dr. Ahmad Faris is awaiting clinic confirmation.', appointmentId: 'a3', createdAt: `${rel(-1)}T10:00:00`, read: false },
  { id: 'n3', userId: 'u1', type: 'confirmed', title: 'Appointment confirmed', body: 'Dr. Priya Nair has confirmed your appointment.', appointmentId: 'a2', createdAt: `${rel(-19)}T15:20:00`, read: true },
];
